/**
 * Variant-Bundle Synchronization Script
 * 
 * This script synchronizes variant selection between:
 * 1. product-variant-picker.liquid (main variant selector)
 * 2. product-buttons.liquid (bundle variant selectors)
 * 
 * Rules:
 * - When #1 variant is selected → #2 and #3 change accordingly
 * - When #2 variant is selected → #3 changes but #1 doesn't change
 * - When #3 variant is selected → #1 and #2 don't change
 */

document.addEventListener('DOMContentLoaded', function() {
  // Configuration
  const SYNC_CONFIG = {
    retryDelay: 100,
    maxRetries: 10
  };

  // Main synchronization class
  class VariantBundleSync {
    constructor() {
      this.isUpdating = false;
      this.variantSelects = null;
      this.bundleDropdowns = [];
      this.currentBundle = null;
      this.initializeSync();
    }

    initializeSync() {
      this.findElements();
      this.setupEventListeners();
    }

    isMainProduct(select) {
      return select.hasAttribute('is-main-product');
    }

    findElements() {
      // Find variant-selects element (main product variant picker)
      this.variantSelects = document.querySelector('variant-selects');
      if (!this.variantSelects) {
        return;
      }

      // Find bundle dropdowns
      this.bundleDropdowns = document.querySelectorAll('.bundle-option .custom-dropdown');

      // Find current selected bundle
      this.getCurrentBundle();
    }

    getCurrentBundle() {
      const selectedBundle = document.querySelector('input[name="bundle_choice"]:checked');
      if (selectedBundle) {
        this.currentBundle = parseInt(selectedBundle.value);
      }
    }

    setupEventListeners() {
      // Listen for variant changes from main variant picker
      if (this.variantSelects) {
        this.variantSelects.addEventListener('change', (e) => {
          if (!this.isUpdating) {
            this.syncMainVariantToBundles();
            
            // Update add to cart button state after main variant change
            setTimeout(() => {
              if (typeof updateAddToCartButtonState === 'function') {
                updateAddToCartButtonState();
              }
            }, 100);
          }
        });
      }

      // Listen for bundle selection changes
      document.addEventListener('change', (e) => {
        if (e.target.name === 'bundle_choice') {
          this.currentBundle = parseInt(e.target.value);
          this.refreshBundleDropdowns();
        }
      });

      // Listen for bundle variant dropdown changes
      document.addEventListener('change', (e) => {
        if (e.target.classList.contains('variant-select') && !this.isUpdating) {
          console.log('syncBundleVariants');
          this.syncBundleVariants(e.target);
          
          // Update add to cart button state after variant change
          setTimeout(() => {
            if (typeof updateAddToCartButtonState === 'function') {
              updateAddToCartButtonState();
            }
          }, 100);
        }
      });
    }

    syncMainVariantToBundles() {
      if (!this.variantSelects) return;

      this.isUpdating = true;

      try {
        // Get current variant options from main picker
        const mainOptions = this.getMainVariantOptions();
        if (!mainOptions.length) {
          return;
        }

        // Sync to ALL bundles (BUY 1, BUY 2, BUY 3) - main product only
        this.updateAllBundleVariants(1, mainOptions); // Update item #1 in all bundles (main product only)
        this.updateAllBundleVariants(2, mainOptions); // Update item #2 in all bundles (main product only)
        this.updateAllBundleVariants(3, mainOptions); // Update item #3 in all bundles (main product only)

        // Update bundle product images for main product
        this.updateBundleProductImages(1, mainOptions);

      } finally {
        setTimeout(() => {
          this.isUpdating = false;
        }, 50);
      }
    }

    syncBundleVariants(changedSelect) {
      this.isUpdating = true;

      try {
        // Determine which item number was changed
        const itemNumber = this.getItemNumberFromSelect(changedSelect);
        if (!itemNumber) {
          return;
        }

        // Get the product ID of the changed select
        const changedProductId = this.getProductIdFromSelect(changedSelect);
        console.log('changedProductId', changedProductId);
        console.log('changedSelect', changedSelect);
        // Debug logging
        console.log('Bundle sync triggered:', {
          itemNumber,
          changedProductId,
          isMainProduct: !changedProductId
        });
        
        // Get the changed variant options
        const changedOptions = this.getBundleItemOptions(itemNumber, changedSelect);

        if (itemNumber === 1) {
          console.log('itemNumber === 1');
          console.log('itemNumber === 1', changedProductId, !changedProductId);
          console.log('itemNumber === 1', changedSelect, changedSelect.hasAttribute('is-main-product'));
          if (!changedProductId) {
            // Main product: sync #1, #2, #3 across all bundles, update main picker too
            this.updateMainVariantFromBundle(changedOptions);
            this.updateAllBundleVariants(1, changedOptions); // Update item #1 in all bundles (main product only)
            this.updateAllBundleVariants(2, changedOptions); // Update item #2 in all bundles (main product only)
            this.updateAllBundleVariants(3, changedOptions); // Update item #3 in all bundles (main product only)
            // Update bundle product images for main product
            this.updateBundleProductImages(1, changedOptions);
          } else {
            // Second product: only sync #1 of the same product across bundles
            this.updateAllBundleVariantsForProduct(1, changedOptions, changedProductId);
            // Update bundle product images for second product
            this.updateBundleProductImages(1, changedOptions);
          }
        } else if (itemNumber === 2) {
          // Rule: #2 changes → #3 changes in all bundles
          // Only sync if it's the same product
          if (!changedProductId) {
            // Main product: sync #2 and #3, update main picker too
            this.updateMainVariantFromBundle(changedOptions);
            this.updateAllBundleVariants(2, changedOptions); // Update item #2 in all bundles (main product only)
            this.updateAllBundleVariants(3, changedOptions); // Update item #3 in all bundles (main product only)
            // Update bundle product images for main product
            this.updateBundleProductImages(2, changedOptions);
          } else {
            // Second product: only sync #2 and #3 of the same product across bundles
            this.updateAllBundleVariantsForProduct(2, changedOptions, changedProductId);
            this.updateAllBundleVariantsForProduct(3, changedOptions, changedProductId);
            // Update bundle product images for second product
            this.updateBundleProductImages(2, changedOptions);
          }
        } else if (itemNumber === 3) {
          // Rule: #3 changes → sync item #3 across all bundles
          // Only sync if it's the same product
          if (!changedProductId) {
            // Main product: sync #3, update main picker too
            this.updateMainVariantFromBundle(changedOptions);
            this.updateAllBundleVariants(3, changedOptions); // Update item #3 in all bundles (main product only)
            // Update bundle product images for main product
            this.updateBundleProductImages(3, changedOptions);
          } else {
            // Second product: only sync #3 of the same product across bundles
            this.updateAllBundleVariantsForProduct(3, changedOptions, changedProductId);
            // Update bundle product images for second product
            this.updateBundleProductImages(3, changedOptions);
          }
        }

      } finally {
        setTimeout(() => {
          this.isUpdating = false;
        }, 50);
      }
    }

    getMainVariantOptions() {
      if (!this.variantSelects) return [];

      const options = [];
      const inputs = this.variantSelects.querySelectorAll('.product-form__input');

      inputs.forEach((input) => {
        const typeElement = input.querySelector('.product-form__input__type');
        if (!typeElement) return;

        const type = typeElement.dataset.type;
        let value = null;

        if (type === 'dropdown') {
          const select = input.querySelector('.select__select');
          value = select ? select.value : null;
        } else if (type === 'swatches') {
          const checked = input.querySelector('input[type="radio"]:checked');
          value = checked ? checked.value : null;
        } else {
          // pills or other types
          const checked = input.querySelector('input:checked');
          value = checked ? checked.value : null;
        }

        if (value) {
          options.push(value);
        }
      });

      return options;
    }

    updateMainVariantFromBundle(options) {
      if (!this.variantSelects || !options.length) return;

      const inputs = this.variantSelects.querySelectorAll('.product-form__input');
      
      inputs.forEach((input, index) => {
        if (index >= options.length) return;

        const typeElement = input.querySelector('.product-form__input__type');
        if (!typeElement) return;

        const type = typeElement.dataset.type;
        const newValue = options[index];

        if (type === 'dropdown') {
          const select = input.querySelector('.select__select');
          if (select && select.value !== newValue) {
            select.value = newValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else if (type === 'swatches') {
          const targetInput = input.querySelector(`input[value="${newValue}"]`);
          if (targetInput && !targetInput.checked) {
            targetInput.checked = true;
            targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          // pills or other types
          const targetInput = input.querySelector(`input[value="${newValue}"]`);
          if (targetInput && !targetInput.checked) {
            targetInput.checked = true;
            targetInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    }

    getItemNumberFromSelect(select) {
      // Extract item number from select name attribute
      // Format: bundle{X}_option{Y}_{Z} where Z is the item number
      const name = select.name;
      const match = name.match(/bundle\d+_option\d+_(\d+)/);
      return match ? parseInt(match[1]) : null;
    }

    getBundleNumberFromSelect(select) {
      // Extract bundle number from select name attribute
      // Format: bundle{X}_option{Y}_{Z} where X is the bundle number
      const name = select.name;
      const match = name.match(/bundle(\d+)_option\d+_\d+/);
      return match ? parseInt(match[1]) : null;
    }

    getProductIdFromSelect(select) {
      // Get product ID from data-product-id attribute
      return select.hasAttribute('data-product-id') ? parseInt(select.getAttribute('data-product-id')) : null;
    }

    getBundleItemOptions(itemNumber, changedSelect = null) {
      // If we have a changed select, get options from that bundle
      // Otherwise, try to use currentBundle or find any available bundle
      let bundleNumber = this.currentBundle;
      
      if (changedSelect) {
        bundleNumber = this.getBundleNumberFromSelect(changedSelect);
      }
      
      if (!bundleNumber) return [];

      const options = [];
      let optionIndex = 1;

      while (true) {
        const select = document.querySelector(`select[name="bundle${bundleNumber}_option${optionIndex}_${itemNumber}"]`);
        if (!select) break;

        options.push(select.value);
        optionIndex++;
      }

      return options;
    }

    updateBundleVariants(itemNumber, options, bundleNumbers = [this.currentBundle], includeSecondProduct = false) {
      if (!options.length) return;

      bundleNumbers.forEach(bundleNumber => {
        if (!bundleNumber) return;
        
        options.forEach((optionValue, optionIndex) => {
          // Update main product variants (without data-product-id)
          const select = document.querySelector(`select[name="bundle${bundleNumber}_option${optionIndex + 1}_${itemNumber}"]:not([data-product-id])`);
          if (select && select.value !== optionValue) {
            // Update hidden select
            select.value = optionValue;

            // Update custom dropdown UI
            const customDropdown = select.closest('.custom-dropdown');
            if (customDropdown) {
              this.updateCustomDropdownUI(customDropdown, optionValue);
            }

            // Dispatch change event
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }

          // Update second product variants only if explicitly requested
          if (includeSecondProduct) {
            const secondProductSelect = document.querySelector(`select[name="bundle${bundleNumber}_option${optionIndex + 1}_${itemNumber}"][data-product-id]`);
            if (secondProductSelect && secondProductSelect.value !== optionValue) {
              // Update hidden select
              secondProductSelect.value = optionValue;

              // Update custom dropdown UI
              const secondProductDropdown = secondProductSelect.closest('.custom-dropdown');
              if (secondProductDropdown) {
                this.updateCustomDropdownUI(secondProductDropdown, optionValue);
              }

              // Dispatch change event
              secondProductSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        });
      });
    }

    updateAllBundleVariants(itemNumber, options) {
      this.updateBundleVariants(itemNumber, options, [1, 2, 3]);
    }

    updateAllBundleVariantsForProduct(itemNumber, options, productId) {
      // Only update variants of the same product across all bundles
      [1, 2, 3].forEach(bundleNumber => {
        if (!bundleNumber) return;
        
        options.forEach((optionValue, optionIndex) => {
          const select = document.querySelector(`select[name="bundle${bundleNumber}_option${optionIndex + 1}_${itemNumber}"][data-product-id="${productId}"]`);
          if (select && select.value !== optionValue) {
            // Update hidden select
            select.value = optionValue;

            // Update custom dropdown UI
            const customDropdown = select.closest('.custom-dropdown');
            if (customDropdown) {
              this.updateCustomDropdownUI(customDropdown, optionValue);
            }

            // Dispatch change event
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      });
    }

    updateCustomDropdownUI(dropdown, newValue) {
      const dropdownText = dropdown.querySelector('.dropdown-text');
      const dropdownItems = dropdown.querySelectorAll('.dropdown-item');

      // Update displayed text
      if (dropdownText) {
        dropdownText.textContent = newValue;
      }

      // Update selected state
      dropdownItems.forEach(item => {
        item.classList.remove('selected');
        if (item.getAttribute('data-value') === newValue) {
          item.classList.add('selected');
        }
      });
    }

    // Function to update bundle product images when variants change
    updateBundleProductImages(itemNumber, variantOptions) {
      // Only update if we have bundle with second product
      const bundleProductsContainer = document.querySelector('.bundle-products-container');
      if (!bundleProductsContainer) return;

      // Check if we have second product data for item 2
      if (itemNumber === 2 && !window.bundleSecondProductData) return;

      // Get the product item element
      const productItem = bundleProductsContainer.querySelector(`[data-item="${itemNumber}"]`);
      if (!productItem) return;

      const productImage = productItem.querySelector('.bundle-product-img');
      console.log('productImage', productImage, itemNumber === 1);
      
      if (!productImage) return;

      // Determine which product data to use
      const isMainProduct = itemNumber === 1;
      const checkIsMainProduct = productItem.hasAttribute('is-main-product');
      const productData = this.getProductData(isMainProduct, checkIsMainProduct);
      console.log('productData', productData, isMainProduct, checkIsMainProduct);
      if (!productData || !productData.variants) return;

      console.log('productData', productData, isMainProduct);
      console.log('variantOptions', variantOptions);

      // Find matching variant
      const matchingVariant = productData.variants.find(variant => {
        return variantOptions.every((option, index) => {
          // Handle different formats: string, object with .value, or object with .optionIndex
          let optionValue;
          if (typeof option === 'string') {
            optionValue = option;
          } else if (option.value !== undefined) {
            optionValue = option.value;
          } else {
            // This shouldn't happen, but handle gracefully
            optionValue = option;
          }
          return variant.options[index] === optionValue;
        });
      });

      console.log('matchingVariant', matchingVariant);

      if (matchingVariant && matchingVariant.featured_media) {
        // Update bundle product image
        const newImageUrl = matchingVariant.featured_media.preview_image.src;
        const newImageAlt = matchingVariant.featured_media.alt || productData.title;
        
        console.log('Updating bundle product image:', {
          itemNumber,
          variantOptions,
          matchingVariant: matchingVariant.id,
          newImageUrl,
          newImageAlt
        });
        
        productImage.src = newImageUrl;
        productImage.alt = newImageAlt;
        productImage.setAttribute('data-variant-image', matchingVariant.featured_media.id);
        
        // Add smooth transition effect
        productImage.style.opacity = '0.7';
        setTimeout(() => {
          productImage.style.opacity = '1';
        }, 150);
      } else {
        console.warn('No matching variant found for bundle product image update:', {
          itemNumber,
          variantOptions,
          productData: productData ? productData.id : 'null',
          variantsCount: productData ? productData.variants.length : 0
        });
      }
    }

    // Helper function to get product data
    getProductData(isMainProduct, checkIsMainProduct) {
      console.log('getProductData', isMainProduct, checkIsMainProduct);
      if (isMainProduct) {
        const productScript = document.querySelector('#ProductJson-product-template');
        if (productScript) {
          try {
            return JSON.parse(productScript.textContent);
          } catch (e) {
            console.error('Error parsing main product JSON:', e);
            return null;
          }
        }
        return window.bundleMainProductData || null;
      } else {
        // Get second product data
        return window.bundleSecondProductData || null;
      }
      return null;
    }

    refreshBundleDropdowns() {
      // Re-find bundle dropdowns when bundle selection changes
      setTimeout(() => {
        this.findElements();
      }, 100);
    }
  }

  // Initialize synchronization with retry mechanism
  function initializeWithRetry(retryCount = 0) {
    if (retryCount >= SYNC_CONFIG.maxRetries) {
      return;
    }

    // Check if required elements exist
    const variantSelects = document.querySelector('variant-selects');
    const bundleOptions = document.querySelector('.bundle-options');

    if (variantSelects && bundleOptions) {
      new VariantBundleSync();
    } else {
      setTimeout(() => {
        initializeWithRetry(retryCount + 1);
      }, SYNC_CONFIG.retryDelay);
    }
  }

  // Start initialization
  initializeWithRetry();
});